import React, { useEffect, useState } from 'react';
import T from 'i18n';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentCaseId, selectCurrentFolder, selectParams } from 'common/selectors';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { components as selectComponents } from 'react-select';
import * as actions from '../../redux/actions';
import { useFetchNotifications } from 'features/case/redux/fetchNotifications';
import { Spinner } from 'features/common';
import SelectFormAdapter from 'features/common/SelectFormAdapter';
import { Col, Form, Row } from 'react-bootstrap';
import { Button, Checkbox, FormControlLabel } from '@mui/material';
import saveAs from 'save-as';
import * as actionsV from 'features/viewing/redux/actions';
import { Modal } from 'features/common/MaterialBasedComponents';
import { useUpdateUserSettings } from 'features/common/redux/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import {
  getNotificationTimestamp,
  getLatestNotification,
  doesNotificationMatchJobId,
  getNotificationDownloadLink,
  getFileNameFromLink,
} from './notificationDownloadUtils';

const DOWNLOAD_NAMING_STRATEGY_DEFAULT = 'default';
const DOWNLOAD_NAMING_STRATEGY_ORIGINAL = 'original';
const DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME = 'folderSpecificName';
const DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_ONLY = 'bundleTabOnly';

const normalizeNamingKey = (value) =>
  value === null || typeof value === 'undefined' ? '' : String(value).trim().toLowerCase();

const resolveDownloadNaming = (filenameSelector) => {
  const normalizedSelector = normalizeNamingKey(filenameSelector);
  if (normalizedSelector === 'original') {
    return {
      namingStrategy: DOWNLOAD_NAMING_STRATEGY_ORIGINAL,
      prependBundleTab: false,
    };
  }

  if (
    normalizedSelector === normalizeNamingKey(DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME) ||
    normalizedSelector === 'folderspecific'
  ) {
    return {
      namingStrategy: DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME,
      prependBundleTab: false,
    };
  }

  if (normalizedSelector === 'bundletab' || normalizedSelector === 'bundletabonly') {
    return {
      namingStrategy: DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_ONLY,
      prependBundleTab: false,
    };
  }

  if (normalizedSelector === 'bundletabfilename') {
    // Legacy bundleTabFilename semantics.
    return {
      namingStrategy: DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME,
      prependBundleTab: true,
    };
  }

  return {
    namingStrategy: DOWNLOAD_NAMING_STRATEGY_DEFAULT,
    prependBundleTab: false,
  };
};

export const FileDownload = ({ show, handleClose, selectedRows }) => {
  const dispatch = useDispatch();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [fileVersions, setFileVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [queuedDownload, setQueuedDownload] = useState(null);
  const { notifications, transientNotifications, fetchNotifications, addPendingDownload } = useFetchNotifications();
  const { file } = useSelector(selectParams);
  const currentFolder = useSelector(selectCurrentFolder);
  const currentCaseId = useSelector(selectCurrentCaseId);
  const { userSettings, updateUserSettings } = useUpdateUserSettings();
  const activeVersion = fileVersions.find((itm) => itm.active);
  const containsPublicHyperlink = !!selectedRows[0]?.hasPublicHyperlinks;
  const containsCrossRefs = Number(activeVersion?.crossRefCount || 0) > 0;
  const normalizeSourceKey = (source) =>
    source === null || typeof source === 'undefined' ? '' : String(source).trim().toLowerCase();
  const versionSources = fileVersions
    .map((version) => normalizeSourceKey(version?.source))
    .filter(Boolean);
  const isMergeOnlySource =
    versionSources.length > 0 && versionSources.every((source) => source === 'merge');

  const getVersionOptionValue = (version, index) => {
    const versionId = version?.id;
    if (versionId !== null && typeof versionId !== 'undefined' && String(versionId).trim()) {
      return `id:${String(versionId).trim()}`;
    }
    return `idx:${index}`;
  };

  const getVersionByOptionValue = (optionValue, versions = fileVersions) => {
    if (!optionValue) return null;

    const normalizedValue = String(optionValue);
    if (normalizedValue.startsWith('id:')) {
      const idValue = normalizedValue.slice(3);
      return versions.find((version) => String(version?.id ?? '') === idValue) || null;
    }

    if (normalizedValue.startsWith('idx:')) {
      const indexValue = Number(normalizedValue.slice(4));
      return Number.isInteger(indexValue) && indexValue >= 0 && indexValue < versions.length
        ? versions[indexValue]
        : null;
    }

    // Backward compatibility for older saved form values that used source.
    return versions.find((version) => String(version?.source ?? '') === normalizedValue) || null;
  };

  const defaultFileName = 'folderSpecificName';
  const downloadSettingsByCase = userSettings?.dataRoom?.downloadSettingsByCase || {};
  const savedDownloadSettings =
    currentCaseId && downloadSettingsByCase[currentCaseId]
      ? downloadSettingsByCase[currentCaseId]
      : {};
  const hasSavedDownloadSettings = Object.keys(savedDownloadSettings).length > 0;
  const savedFileSource = savedDownloadSettings.fileSource || '';
  const savedIncludeFileDetails = savedDownloadSettings.includeFileDetails ?? true;
  const savedIncludeExternalId = savedDownloadSettings.includeExternalId ?? false;
  const savedIncludeCrossRef = savedDownloadSettings.includeCrossRef ?? false;
  const savedConvertCrossRefToHyperlink =
    typeof savedDownloadSettings.convertCrossRefToHyperlink === 'boolean'
      ? savedDownloadSettings.convertCrossRefToHyperlink
      : false;
  const savedIncludePublicHyperlinks =
    typeof savedDownloadSettings.includePublicHyperlinks === 'boolean'
      ? savedDownloadSettings.includePublicHyperlinks
      : false;

  const getFileNameDisplayName = (option) => {
    return T.translate(option.name);
  };

  const getFileSourceLabel = (source) => {
    if (source === 'ocr') {
      return T.translate('generic.downloadFormatOcr');
    }
    if (source === 'original (pdf)') {
      return T.translate('generic.downloadFormatOriginalPdf');
    }
    if (source === 'tiff') {
      return T.translate('generic.downloadFormatTiff');
    }
    return source;
  };

  const formatVersionCreatedAt = (createdAt) => {
    if (!createdAt) return '';
    const parsedDate = new Date(createdAt);
    if (Number.isNaN(parsedDate.getTime())) return '';
    return parsedDate.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getVersionCreatedAt = (version) =>
    version?.createdAt ||
    version?.created ||
    version?.createdDate ||
    version?.dateCreated ||
    version?.createdOn ||
    null;

  const VersionOption = (props) => {
    const { data } = props;
    return (
      <selectComponents.Option {...props}>
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
          {data.createdAtLabel && (
            <div style={{ fontSize: '0.72rem', color: '#6b7280', marginBottom: '0.1rem' }}>
              {data.createdAtLabel}
            </div>
          )}
          <div>{data.label}</div>
        </div>
      </selectComponents.Option>
    );
  };

  const fileNameOptions = [
    {
      name: 'case.downloadFolderModal.fileNameOptions.fileName',
      fileName: 'folderSpecificName',
    },
    {
      name: 'case.downloadFolderModal.fileNameOptions.originalFileName',
      fileName: 'original',
    },
    { name: 'case.downloadFolderModal.fileNameOptions.bundleTabLabel', fileName: 'bundleTabOnly' },
  ];

  const availableFileNameOptions = new Set(fileNameOptions.map((option) => option.fileName));
  const normalizedSavedFilenameSelector =
    savedDownloadSettings?.filenameSelector === 'bundleTabFilename' ||
    savedDownloadSettings?.filenameSelector === 'bundleTab'
      ? 'bundleTabOnly'
      : savedDownloadSettings?.filenameSelector;
  const savedFilenameSelector = availableFileNameOptions.has(
    normalizedSavedFilenameSelector,
  )
    ? normalizedSavedFilenameSelector
    : defaultFileName;

  const { reset, handleSubmit, control, setValue } = useForm({
    defaultValues: {
      fileSource: '',
      burnId: savedIncludeFileDetails,
      burnExternalId: savedIncludeExternalId,
      includeFileDetails: savedIncludeFileDetails,
      filenameSelector: savedFilenameSelector,
      burnCrossRef: savedIncludeCrossRef,
      hyperlink: savedConvertCrossRefToHyperlink,
      publichyperlink: savedIncludePublicHyperlinks,
      saveDownloadSettings: false,
    },
  });
  const burnCrossRefValue = useWatch({ control, name: 'burnCrossRef' });
  const showHyperlinkToggle = !!burnCrossRefValue;

  const downloadActiveFileVersion = (data) => {
    const selectedVersionEntry = getVersionByOptionValue(data.fileSource) || activeVersion;
    if (!selectedVersionEntry) {
      return;
    }
    const includeFileDetailsValue = isMergeOnlySource ? false : data.includeFileDetails;
    const includeCrossRefsValue = containsCrossRefs ? !!data.burnCrossRef : false;
    const convertCrossRefsToHyperlinksValue = includeCrossRefsValue ? !!data.hyperlink : false;
    const includePublicHyperlinksValue = !!data.publichyperlink;
    const { namingStrategy, prependBundleTab } = resolveDownloadNaming(data.filenameSelector);
    const selectedVersionSource = selectedVersionEntry?.source || '';
    const selectedVersionId = selectedVersionEntry?.id;
    setLoading(true);
    if (data.saveDownloadSettings) {
      const safeUserSettings = userSettings || {};
      const currentDataRoom = safeUserSettings.dataRoom || {};
      const currentDownloadSettingsByCase = currentDataRoom.downloadSettingsByCase || {};
      updateUserSettings({
        userSettings: {
          ...safeUserSettings,
          dataRoom: {
            ...currentDataRoom,
            downloadSettingsByCase: {
              ...currentDownloadSettingsByCase,
              ...(currentCaseId && {
                [currentCaseId]: {
                  fileSource: selectedVersionSource,
                  includeFileDetails: includeFileDetailsValue,
                  includeExternalId: data.burnExternalId,
                  filenameSelector: data.filenameSelector || defaultFileName,
                  includeCrossRef: includeCrossRefsValue,
                  convertCrossRefToHyperlink: convertCrossRefsToHyperlinksValue,
                  includePublicHyperlinks: includePublicHyperlinksValue,
                },
              }),
            },
          },
        },
      });
    }
    dispatch(
      actionsV.downloadDocument({
        fileId: selectedRows[0].id || file,
        version: selectedVersionSource,
        versionId: selectedVersionId,
        fileType: selectedRows[0].type,
        burnId: includeFileDetailsValue ? data.burnId : false,
        burnExternalId: data.burnExternalId,
        burnCrossRef: includeCrossRefsValue,
        hyperlink: convertCrossRefsToHyperlinksValue,
        publichyperlink: includePublicHyperlinksValue,
        namingStrategy,
        prependBundleTab,
        filenameSelector: data.filenameSelector || defaultFileName,
        includeFileDetails: includeFileDetailsValue,
        ...(currentFolder?.id && { folderId: currentFolder.id }),
      }),
    ).then((response) => {
      if (!response) {
        setLoading(false);
        return;
      }
      const isQueued =
        response.status && String(response.status).trim().toLowerCase() === 'queued';
      if (isQueued) {
        const jobId = response.jobId ? String(response.jobId) : '';
        setQueuedDownload({
          jobId,
          status: 'queued',
          message: T.translate('case.downloadFolderModal.processingWaiting'),
          link: '',
          notificationId: null,
          autoDownloadState: 'idle',
          queuedAt: Date.now(),
        });
        if (jobId) {
          addPendingDownload({ jobId, label: selectedRows[0]?.id || file || '' });
        }
        setLoading(false);
        return;
      }
      saveAs(response.blob, response.fileName || selectedRows[0].id || file);
      setTimeout(() => {
        handleClose();
        setLoading(false);
      }, 500);
    }).catch(() => {
      setLoading(false);
    });
  };

  const updateFileVersions = (optionValue, versions = fileVersions) => {
    const updatedFileVersions = versions.map((item, index) => {
      if (getVersionOptionValue(item, index) === optionValue) return { ...item, active: true };
      return { ...item, active: false };
    });
    setFileVersions(updatedFileVersions);
  };

  useEffect(() => {
    dispatch(actions.fetchFileVersions({ fileId: selectedRows[0].id || file }))
      .then((res) => {
        const allVersions = Array.isArray(res) ? res : [];
        const savedVersion = savedFileSource
          ? allVersions.find((item) => item.source === savedFileSource)
          : null;
        const defaultSearchableVersion = !hasSavedDownloadSettings
          ? allVersions.find((item) =>
              ['ocr', 'recent-ocr'].includes((item.source || '').toLowerCase()),
            )
          : null;
        const defaultOriginalVersion = !hasSavedDownloadSettings
          ? allVersions.find((item) =>
              ['original', 'original (pdf)'].includes((item.source || '').toLowerCase()),
            )
          : null;
        const preferredVersion =
          savedVersion ||
          defaultSearchableVersion ||
          defaultOriginalVersion ||
          allVersions.find((item) => item.active) ||
          allVersions[0];
        const preferredIndex = preferredVersion ? allVersions.indexOf(preferredVersion) : -1;
        const preferredValue =
          preferredIndex > -1 ? getVersionOptionValue(preferredVersion, preferredIndex) : '';
        const updated = allVersions.map((item, index) => ({
          ...item,
          active: preferredValue ? getVersionOptionValue(item, index) === preferredValue : false,
        }));
        setFileVersions(updated);
        if (preferredValue) {
          setSelectedVersion(preferredValue);
          setValue('fileSource', preferredValue);
        }
        setValue('filenameSelector', savedFilenameSelector);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [show, selectedRows, dispatch, file, setValue, savedFileSource, savedFilenameSelector]);

  useEffect(() => {
    if (show) {
      reset({
        fileSource: selectedVersion || '',
        burnId: savedIncludeFileDetails,
        burnExternalId: savedIncludeExternalId,
        includeFileDetails: savedIncludeFileDetails,
        filenameSelector: savedFilenameSelector,
        burnCrossRef: savedIncludeCrossRef,
        hyperlink: savedConvertCrossRefToHyperlink,
        publichyperlink: savedIncludePublicHyperlinks,
        saveDownloadSettings: false,
      });
    }
  }, [
    show,
    reset,
    savedIncludeFileDetails,
    savedIncludeExternalId,
    savedIncludeCrossRef,
    savedConvertCrossRefToHyperlink,
    savedIncludePublicHyperlinks,
    savedFilenameSelector,
    selectedVersion,
  ]);

  useEffect(() => {
    if (show && isMergeOnlySource) {
      setValue('includeFileDetails', false);
      setValue('burnId', false);
    }
  }, [show, isMergeOnlySource, setValue]);

  useEffect(() => {
    if (show && !containsCrossRefs) {
      setValue('burnCrossRef', false);
      setValue('hyperlink', false);
    }
  }, [show, containsCrossRefs, setValue]);

  useEffect(() => {
    if (show && !containsPublicHyperlink) {
      setValue('publichyperlink', false);
    }
  }, [show, containsPublicHyperlink, setValue]);

  useEffect(() => {
    if (show) {
      setQueuedDownload(null);
    }
  }, [show]);

  useEffect(() => {
    if (!queuedDownload?.jobId || queuedDownload?.link) {
      return;
    }

    fetchNotifications();
    const intervalId = window.setInterval(() => {
      fetchNotifications();
    }, 15000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [queuedDownload?.jobId, queuedDownload?.link, fetchNotifications]);

  useEffect(() => {
    if (!queuedDownload?.jobId) return;

    const allNotifications = [...(notifications || []), ...(transientNotifications || [])];
    if (!allNotifications.length) return;

    const matchingNotifications = allNotifications.filter((notification) =>
      doesNotificationMatchJobId(notification, queuedDownload.jobId),
    );

    const fallbackDownloadNotifications = allNotifications.filter((notification) => {
      const notificationLink = getNotificationDownloadLink(notification);
      if (!notificationLink) return false;
      if (queuedDownload?.queuedAt) {
        const notificationTimestamp = getNotificationTimestamp(notification);
        if (notificationTimestamp !== null && notificationTimestamp + 10000 < queuedDownload.queuedAt) {
          return false;
        }
      }
      return true;
    });

    const candidateNotifications = matchingNotifications.length
      ? matchingNotifications
      : fallbackDownloadNotifications;
    if (!candidateNotifications.length) return;

    const latestMatch = getLatestNotification(candidateNotifications);
    if (!latestMatch) return;

    const nextStatus = latestMatch?.status || queuedDownload.status;
    const nextMessage = latestMatch?.message || queuedDownload.message;
    const nextLink = getNotificationDownloadLink(latestMatch) || queuedDownload.link;
    const nextNotificationId = latestMatch?.id || queuedDownload.notificationId;

    setQueuedDownload((prev) => {
      if (!prev) return prev;
      if (
        prev.status === nextStatus &&
        prev.message === nextMessage &&
        prev.link === nextLink &&
        prev.notificationId === nextNotificationId
      ) {
        return prev;
      }

      return {
        ...prev,
        status: nextStatus,
        message: nextMessage,
        link: nextLink,
        notificationId: nextNotificationId,
      };
    });
  }, [queuedDownload?.jobId, queuedDownload?.link, notifications, transientNotifications]);

  useEffect(() => {
    if (!queuedDownload?.link || queuedDownload?.autoDownloadState !== 'idle') return;

    let isCancelled = false;

    const runAutoDownload = async () => {
      const fallbackName = `${selectedRows[0]?.id || file || 'download'}.pdf`;
      const fileName = getFileNameFromLink(queuedDownload.link, fallbackName);

      try {
        const response = await fetch(queuedDownload.link);
        if (!response.ok) throw new Error(`Unexpected status code ${response.status}`);
        const blob = await response.blob();
        if (isCancelled) return;
        saveAs(blob, fileName);
        setQueuedDownload((prev) => (prev ? { ...prev, autoDownloadState: 'started' } : prev));
      } catch (error) {
        console.error('Auto-download from notification link failed', error);
        if (isCancelled) return;
        setQueuedDownload((prev) => (prev ? { ...prev, autoDownloadState: 'failed' } : prev));
      }
    };

    runAutoDownload();

    return () => {
      isCancelled = true;
    };
  }, [queuedDownload?.link, queuedDownload?.autoDownloadState, selectedRows, file]);

  const handleManualQueuedDownload = () => {
    if (!queuedDownload?.link) return;
    const fallbackName = `${selectedRows[0]?.id || file || 'download'}.pdf`;
    const link = document.createElement('a');
    link.href = queuedDownload.link;
    link.rel = 'noopener noreferrer';
    link.target = '_blank';
    link.download = getFileNameFromLink(queuedDownload.link, fallbackName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Modal
        show={show}
        title={T.translate('generic.download') + ` - ${selectedRows[0].id || file}`}
        handleClose={() => {
          if (loading) setLoading(false);
          setFileVersions([]);
          setSelectedVersion(null);
          setQueuedDownload(null);
          handleClose();
        }}
        saveTitle={!queuedDownload ? T.translate('generic.download') : undefined}
        handleSave={!queuedDownload ? handleSubmit((data) => downloadActiveFileVersion(data)) : undefined}
        disableSave={loading || !activeVersion}
      >
        {queuedDownload ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: '1.05rem',
                color: '#111827',
              }}
            >
              {T.translate('case.downloadFolderModal.processingTitle')}
            </div>
            <div style={{ color: '#374151', lineHeight: 1.45 }}>
              {T.translate('case.downloadFolderModal.processingMessage')}
            </div>

            {!queuedDownload.link ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#4b5563',
                }}
              >
                <Spinner style={{ height: '1.25rem' }} />
                <span>{queuedDownload.message || T.translate('case.downloadFolderModal.processingWaiting')}</span>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ color: '#374151' }}>
                  {queuedDownload.autoDownloadState === 'started'
                    ? T.translate('case.downloadFolderModal.processingAutoDownloadStarted')
                    : T.translate('case.downloadFolderModal.processingAutoDownloadFallback')}
                </div>
                {queuedDownload.message &&
                  queuedDownload.message !== T.translate('case.downloadFolderModal.processingWaiting') && (
                    <div style={{ color: '#4b5563', fontSize: '0.95rem' }}>{queuedDownload.message}</div>
                  )}
                <Button
                  type="button"
                  variant="outlined"
                  color="primary"
                  style={{ width: 'fit-content' }}
                  onClick={handleManualQueuedDownload}
                >
                  {T.translate('case.downloadFolderModal.processingDownloadNow')}
                </Button>
              </div>
            )}
          </div>
        ) : !loading &&
          (fileVersions && fileVersions.length > 0 ? (
            <Form
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <Form.Group>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <Form.Label
                    style={{
                      fontWeight: 700,
                      fontSize: '1rem',
                      color: '#111827',
                      marginBottom: '0.35rem',
                    }}
                  >
                    {T.translate('generic.downloadFormat')}
                  </Form.Label>
                  <SelectFormAdapter
                    key={`format-${selectedVersion || 'none'}`}
                    control={control}
                    name="fileSource"
                    label=""
                    options={fileVersions.map((version, index) => ({
                      label: getFileSourceLabel(version.source),
                      value: getVersionOptionValue(version, index),
                      createdAtLabel: formatVersionCreatedAt(getVersionCreatedAt(version)),
                    }))}
                    defaultValue={selectedVersion || ''}
                    placeholder={T.translate('generic.selectFileSource')}
                    customOption={VersionOption}
                    onChange={(value) => {
                      setSelectedVersion(value);
                      updateFileVersions(value);
                    }}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                  {/* <hr style={{ width: '100%', border: '0', borderTop: '1px solid #e5e7eb' }} /> */}
                  <button
                    type="button"
                    onClick={() => setShowAdvanced((prev) => !prev)}
                    style={{
                      alignSelf: 'flex-start',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      background: 'transparent',
                      border: 'none',
                      color: '#748CEC',
                      fontWeight: 700,
                      padding: 0,
                      cursor: 'pointer',
                    }}
                  >
                    <span>
                      {showAdvanced
                        ? T.translate('generic.hideAdvancedSettings')
                        : T.translate('generic.showAdvancedSettings')}
                    </span>
                    <FontAwesomeIcon icon={showAdvanced ? faChevronUp : faChevronDown} size="sm" />
                  </button>

                  {showAdvanced && (
                    <>
                      <div
                        style={{
                          color: '#6b7280',
                          fontSize: '0.9rem',
                        }}
                      >
                        {T.translate('generic.downloadFormatHelper')}
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <Form.Label
                          style={{
                            fontWeight: 700,
                            fontSize: '1rem',
                            color: '#111827',
                            marginBottom: '0.25rem',
                          }}
                        >
                          {T.translate('generic.downloadDetails')}
                        </Form.Label>
                        {!isMergeOnlySource && (
                          <Controller
                            control={control}
                            name="includeFileDetails"
                            render={({ field }) => (
                              <FormControlLabel
                                sx={{ m: 0 }}
                                control={
                                  <Checkbox
                                    checked={!!field.value}
                                    onChange={(event, checked) => {
                                      field.onChange(checked);
                                      setValue('burnId', checked);
                                    }}
                                    defaultChecked
                                  />
                                }
                                label={T.translate('generic.downloadIncludeFileDetails')}
                              />
                            )}
                          />
                        )}
                        <Controller
                          control={control}
                          name="burnExternalId"
                          render={({ field }) => (
                            <FormControlLabel
                              sx={{ m: 0 }}
                              control={
                                <Checkbox
                                  checked={!!field.value}
                                  onChange={(event, checked) => field.onChange(checked)}
                                />
                              }
                              label={T.translate('generic.downloadIncludeExternalId')}
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name="burnCrossRef"
                          render={({ field }) => (
                            <FormControlLabel
                              sx={{ m: 0 }}
                              control={
                                <Checkbox
                                  checked={!!field.value}
                                  onChange={(event, checked) => {
                                    field.onChange(checked);
                                    if (!checked) {
                                      setValue('hyperlink', false);
                                    }
                                  }}
                                  disabled={!containsCrossRefs}
                                />
                              }
                              label={T.translate(`case.includeCrossRef`)}
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name="hyperlink"
                          render={({ field }) => (
                            <FormControlLabel
                              sx={{ m: 0 }}
                              control={
                                <Checkbox
                                  checked={!!field.value}
                                  onChange={(event, checked) => field.onChange(checked)}
                                  disabled={!containsCrossRefs || !showHyperlinkToggle}
                                />
                              }
                              label={T.translate(`case.convertCrossRefToExternalHyperlink`)}
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name="publichyperlink"
                          render={({ field }) => (
                            <FormControlLabel
                              sx={{ m: 0 }}
                              control={
                                <Checkbox
                                  checked={!!field.value}
                                  onChange={(event, checked) => field.onChange(checked)}
                                  disabled={!containsPublicHyperlink}
                                />
                              }
                              label={T.translate(`case.includePublicHyperLinks`)}
                            />
                          )}
                        />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                        <Form.Label
                          style={{
                            fontWeight: 700,
                            fontSize: '1rem',
                            color: '#111827',
                            marginBottom: '0.25rem',
                          }}
                        >
                          {T.translate('common.fileName')}
                        </Form.Label>
                        <SelectFormAdapter
                          key={`filename-${defaultFileName || 'none'}`}
                          control={control}
                          name="filenameSelector"
                          label=""
                          options={fileNameOptions.map((value) => ({
                            label: getFileNameDisplayName(value),
                            value: value.fileName,
                          }))}
                          defaultValue={defaultFileName}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                        <div
                          style={{
                            color: '#6b7280',
                            fontSize: '0.9rem',
                          }}
                        >
                          {T.translate('generic.downloadFilenameHelper')}
                        </div>
                      </div>
                      <Controller
                        control={control}
                        name="saveDownloadSettings"
                        render={({ field }) => (
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Checkbox
                                checked={!!field.value}
                                onChange={(event, checked) => field.onChange(checked)}
                              />
                            }
                            label={T.translate('generic.saveDownloadSettings')}
                          />
                        )}
                      />
                    </>
                  )}
                </div>
              </Form.Group>
            </Form>
          ) : (
            T.translate('generic.noFileVersionsFound')
          ))}
        {loading && <Spinner />}
      </Modal>
    </>
  );
};
